import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase } from '../utils/api';

const OIDCSSOButton = ({ email, onError, idpName: propIdpName, primary = false }) => {
  const [loading, setLoading] = useState(false);
  const [oidcAvailable, setOidcAvailable] = useState(!!propIdpName);
  const [idpName, setIdpName] = useState(propIdpName || 'Enterprise SSO');
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    // If idpName was provided as a prop, OIDC is already confirmed available
    if (propIdpName) {
      setOidcAvailable(true);
      setIdpName(propIdpName);
      return;
    }

    // Otherwise, check if OIDC is available for this email domain
    const checkOIDC = async () => {
      if (!email || !email.includes('@')) {
        setOidcAvailable(false);
        return;
      }

      setChecking(true);
      try {
        const apiBase = getApiBase();
        const response = await axios.get(`${apiBase}/auth/oidc/check`, {
          params: { email }
        });

        if (response.data.oidc_enabled) {
          setOidcAvailable(true);
          setIdpName(response.data.idp_name || 'Enterprise SSO');
        } else {
          setOidcAvailable(false);
        }
      } catch (error) {
        console.error('Error checking OIDC availability:', error);
        setOidcAvailable(false);
      } finally {
        setChecking(false);
      }
    };

    // Debounce the check
    const timeoutId = setTimeout(checkOIDC, 500);
    return () => clearTimeout(timeoutId);
  }, [email, propIdpName]);

  const handleOIDCLogin = async () => {
    if (!email || !email.includes('@')) {
      onError?.('Please enter your email address first');
      return;
    }

    setLoading(true);
    try {
      const apiBase = getApiBase();

      // Get the redirect URL from the backend
      const response = await axios.get(`${apiBase}/auth/oidc/login`, {
        params: {
          email,
          redirect_url: window.location.origin
        }
      });

      if (response.data.redirect_url) {
        // Redirect to IdP
        window.location.href = response.data.redirect_url;
      } else {
        throw new Error('No redirect URL received');
      }
    } catch (error) {
      console.error('OIDC login error:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to initiate OIDC login';
      onError?.(message);
      setLoading(false);
    }
  };

  // Only show if OIDC is available for this email domain
  if (!oidcAvailable && !checking) {
    return null;
  }

  // Primary style (prominent) vs secondary style
  const buttonClass = primary
    ? "group relative w-full flex items-center justify-center py-3 px-4 border-2 border-indigo-600 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
    : "group relative w-full flex items-center justify-center py-2.5 px-4 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors";

  return (
    <button
      type="button"
      onClick={handleOIDCLogin}
      disabled={loading || checking || !oidcAvailable}
      className={buttonClass}
    >
      <svg
        className={`w-5 h-5 mr-2 ${primary ? 'text-white' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
        />
      </svg>
      {loading ? (
        'Redirecting to SSO...'
      ) : checking ? (
        'Checking SSO...'
      ) : (
        `Sign in with ${idpName}`
      )}
    </button>
  );
};

export default OIDCSSOButton;
